import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { usePageOwnsChrome } from "./page-chrome";
import { useUiMode } from "./ui-mode";
import { NativePrintReviewGate } from "./print/NativePrintReview";
import { PageLoading } from "./shared/PageLoading";

const CollectionPage = lazy(() => import("./discovery/CollectionPage"));
const BookDetailsPage = lazy(() => import("./discovery/BookDetailsPage"));
const ExampleHubPage = lazy(() => import("./discovery/ExampleHubPage"));
const DiscoveryInfoPage = lazy(() => import("./discovery/DiscoveryInfoPage"));
const LibraryPage = lazy(() => import("./library/LibraryPage"));
const AccountSettingsPage = lazy(() => import("./account/AccountSettingsPage"));
const BookSettingsPage = lazy(() => import("./account/BookSettingsPage"));
const BookFamilyPage = lazy(() => import("./account/BookFamilyPage"));
const RedeemPage = lazy(() => import("./voucher/RedeemPage"));
const StartPage = lazy(() => import("./voucher/StartPage"));
const SupportPage = lazy(() => import("./support/SupportPage"));
const SupportApprovalPage = lazy(() => import("./trust/SupportApprovalPage"));
const UnsubscribePage = lazy(() => import("./trust/UnsubscribePage"));
const PrintReviewPage = lazy(() => import("./print/PrintReviewPage"));
const OrderProgressPage = lazy(() => import("./print/OrderProgressPage"));
const SignIn = lazy(() =>
  import("./access/SignInPage").then((module) => ({ default: module.NextSignInPage })),
);
const SignInComplete = lazy(() =>
  import("./access/SignInPage").then((module) => ({ default: module.NextSignInCompletePage })),
);

/**
 * A redesigned page claims the header and footer as soon as it is chosen, before its code has
 * loaded, so the current site's chrome never shows while it loads.
 */
function OwnChrome({ children }: { children: ReactNode }) {
  usePageOwnsChrome();
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

/** Shows the redesigned page, or the earlier one to a browser that chose it (`?ui=classic`). */
export function Redesign({ next, classic }: { next: ReactNode; classic: ReactNode }) {
  return useUiMode() === "next" ? next : classic;
}

/** A redirect that keeps a design chosen in the address (`?ui=`) from the address it leaves. */
export function RedesignRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  const ui = new URLSearchParams(search).get("ui");
  if (!ui) return <Navigate replace to={to} />;
  const [path, hash = ""] = to.split("#");
  const joiner = path!.includes("?") ? "&" : "?";
  return (
    <Navigate replace to={path + joiner + "ui=" + encodeURIComponent(ui) + (hash && "#" + hash)} />
  );
}

/** Old addresses that the redesign folds into the FAQ, keeping a valid book choice. */
export function VoucherQuestionsRedirect() {
  const { search } = useLocation();
  const books = new URLSearchParams(search).getAll("book");
  const book = books.length === 1 ? books[0] : null;
  const known = ["story", "love", "children", "recipes", "comic"];
  return (
    <RedesignRedirect
      to={"/faq" + (book && known.includes(book) ? "?book=" + book : "") + "#voucher"}
    />
  );
}

export function NextCollectionPage() {
  return (
    <OwnChrome>
      <CollectionPage />
    </OwnChrome>
  );
}

export function NextBookDetailsPage() {
  const { templateId = "" } = useParams();
  return (
    <OwnChrome>
      <BookDetailsPage key={templateId} />
    </OwnChrome>
  );
}

export function NextExampleHubPage() {
  return (
    <OwnChrome>
      <ExampleHubPage />
    </OwnChrome>
  );
}

export function NextHowItWorksPage() {
  return (
    <OwnChrome>
      <DiscoveryInfoPage kind="how" />
    </OwnChrome>
  );
}

export function NextFaqPage() {
  return (
    <OwnChrome>
      <DiscoveryInfoPage kind="faq" />
    </OwnChrome>
  );
}

export function NextRecoveryPage() {
  return (
    <OwnChrome>
      <DiscoveryInfoPage kind="recovery" />
    </OwnChrome>
  );
}

/**
 * My Library at /app. Every older link into /app#… (account settings, a book's privacy and data,
 * reminder preferences in emails, a reward checkout's return from Stripe) is for the account
 * page, which now has its own address: it goes there with its fragment intact.
 */
export function NextLibraryPage() {
  const { hash } = useLocation();
  if (hash.length > 1) return <RedesignRedirect to={"/app/account" + hash} />;
  return (
    <OwnChrome>
      <LibraryPage />
    </OwnChrome>
  );
}

/**
 * Account settings at /app/account. Older links into the classic page's sections (in emails, the
 * book tools page and Stripe's return from a reward checkout) open the matching section here.
 */
export function NextAccountSettingsPage() {
  const { hash } = useLocation();
  const book = /^#book-([A-Za-z0-9_-]{1,200})-settings$/u.exec(hash);
  if (book) return <RedesignRedirect to={`/app/books/${book[1]}/settings`} />;
  if (hash === "#account-settings" || hash === "#story-reminders")
    return <RedesignRedirect to="/app/account" />;
  if (hash.startsWith("#reward-checkout"))
    return <RedesignRedirect to="/app/account?section=rewards" />;
  return (
    <OwnChrome>
      <AccountSettingsPage />
    </OwnChrome>
  );
}

export function NextBookSettingsPage() {
  const { bookId = "" } = useParams();
  return (
    <OwnChrome>
      <BookSettingsPage key={bookId} />
    </OwnChrome>
  );
}

/** The people in a book and its invitations, under the book (E11, 2026-11-02). */
export function NextBookFamilyPage() {
  const { bookId = "" } = useParams();
  return (
    <OwnChrome>
      <BookFamilyPage key={bookId} />
    </OwnChrome>
  );
}

export function NextRedeemPage() {
  return (
    <OwnChrome>
      <RedeemPage />
    </OwnChrome>
  );
}

export function NextStartPage() {
  return (
    <OwnChrome>
      <StartPage />
    </OwnChrome>
  );
}

export function NextSupportPage() {
  return (
    <OwnChrome>
      <SupportPage />
    </OwnChrome>
  );
}

export function NextSupportApprovalPage() {
  return (
    <OwnChrome>
      <SupportApprovalPage />
    </OwnChrome>
  );
}

export function NextUnsubscribePage() {
  return (
    <OwnChrome>
      <UnsubscribePage />
    </OwnChrome>
  );
}

/** A book's settings live on the classic account page for visitors who haven't opted in. */
export function ClassicBookSettingsRedirect() {
  const { bookId = "" } = useParams();
  // The earlier screens are chosen in the address, so the choice goes with it.
  return <RedesignRedirect to={`/app/account#book-${bookId}-settings`} />;
}

export function NextSignInPage() {
  return (
    <OwnChrome>
      <SignIn />
    </OwnChrome>
  );
}

export function NextSignInCompletePage() {
  return (
    <OwnChrome>
      <SignInComplete />
    </OwnChrome>
  );
}

export function NextRetiredCheckoutPage() {
  return (
    <OwnChrome>
      <DiscoveryInfoPage kind="retired-checkout" />
    </OwnChrome>
  );
}

/**
 * A book's exact print review in the redesign. Bedtime's own cover and edition review steps keep
 * their pages (`?stage=`), and so the site's chrome.
 */
export function NextPrintReviewPage({ classic }: { classic: ReactNode }) {
  const stage = new URLSearchParams(useLocation().search).get("stage");
  if (stage === "edition" || stage === "cover") return classic;
  return (
    <OwnChrome>
      <NativePrintReviewGate fallback={<PrintReviewPage />} />
    </OwnChrome>
  );
}

export function NextOrderProgressPage() {
  return (
    <OwnChrome>
      <OrderProgressPage />
    </OwnChrome>
  );
}
